'use client';

import { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn, getFirstLetters } from '@/lib/utils';
import useLoyaltyStore from '@/lib/zustand/store/loyaltyStore';
import LevelUpModal from '@/screens/level-up/level-up-modal';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, User } from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import { DMImage } from '../custom-image/custom-image';
import UserCoin from '../header/components/user-coin';
import { DMLink } from '../link';

const Notch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const session = useSession();
  const { xp } = useLoyaltyStore();
  const { data } = session;
  const isMobile = useIsMobile();

  const notchRef = useRef<any>(null);

  const presence = {
    enter: {
      opacity: 0,
      scale: 0.9,
    },
    center: {
      opacity: 1,
      scale: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: {
        when: 'afterChildren',
        staggerChildren: 0.1,
        staggerDirection: -1,
      },
    },
  };

  const itemVariants = {
    enter: {
      opacity: 0,
      y: 20,
    },
    center: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2,
      },
    },
  };

  const handleToggle = () => {
    if (isOpen) {
      setShowContent(false);
    } else {
      setIsOpen(true);
      setTimeout(() => setShowContent(true), 50);
    }
  };

  // useEffect(() => {
  //   const handleClickOutside = (event: any) => {
  //     if (notchRef?.current && !notchRef?.current?.contains(event.target)) {
  //       if (isOpen) {
  //         handleToggle();
  //       }
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);

  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (notchRef?.current && !notchRef?.current?.contains(event.target)) {
        if (isOpen) {
          handleToggle();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className='relative w-[210px] z-50' onClick={handleToggle}>
      <motion.div
        ref={notchRef}
        whileHover={{
          scale: isOpen ? 1 : 0.95,
        }}
        // className={cn(
        //   'bg-brand-secondary z-50 rounded-full h-14 px-4 cursor-pointer',

        //   isOpen && 'rounded-3xl'
        // )}
        className={cn(
          'bg-brand-secondary w-[160px] rounded-full h-14 px-4 cursor-pointer',
          !isMobile && 'absolute right-0 -top-7 ',
          isMobile && 'absolute left-1/2  -translate-x-1/2 transform z-50',
          isOpen && 'rounded-3xl'
          // isOpen && isMobile && `relative left-1/2 -translate-x-1/2`
        )}
        animate={{ height: isOpen ? 140 : 54, width: isOpen ? 280 : 220 }}
        // animate={{
        //   height: isOpen ? (isMobile ? 140 : 140) : 54,
        //   width: isOpen ? (isMobile ? 280 : 280) : 160,
        // }}
        transition={{ duration: 0.1 }}
      >
        <div
          className={cn('flex items-center justify-between relative h-14', {
            'pt-5': isOpen,
            'pointer-events-none': !isOpen,
          })}
        >
          <div
            className={`flex items-center gap-1 ${isOpen ? 'mt-6 flex-col-reverse' : 'flex-row'}`}
            onClick={isOpen ? handleToggle : () => {}}
          >
            <DMLink
              href='/level-up'
              className='flex items-center gap-1 hover:bg-slate-800 rounded-lg p-1'
            >
              <DMImage src='/images/home/level-up.png' alt='coin Icon' width={20} height={20} />{' '}
              {xp}
            </DMLink>
            <UserCoin />
          </div>
          <LevelUpModal isOpen={isOpen} />

          <motion.div
            className='rounded-3xl overflow-hidden relative'
            animate={{
              height: isOpen ? 90 : 40,
              width: isOpen ? 90 : 40,
              y: isOpen ? 27 : 0,
            }}
            transition={{ duration: 0.4 }}
          >
            <div
              className={cn(
                'transition-all duration-150 flex h-full bg-brand-primary rounded-xl w-full items-center justify-center text-black uppercase',
                isOpen && 'text-5xl'
              )}
              onClick={handleToggle}
            >
              {data?.user?.image ? (
                <DMImage
                  src={data?.user?.image as string}
                  height={100}
                  width={100}
                  className='w-full h-full overflow-clip bg-cover'
                  alt='profile image'
                />
              ) : (
                data?.user?.name && getFirstLetters(data?.user?.name)
              )}
            </div>
          </motion.div>
        </div>
        <AnimatePresence onExitComplete={() => setIsOpen(false)}>
          {showContent && (
            <>
              {/* <motion.ul
                initial='enter'
                animate='center'
                exit='exit'
                variants={presence}
                transition={{ duration: 0.2 }}
                className='text-brand-primary-hover'
              >
                <div className='flex items-center gap-1 font-semibold text-white w-5 h-5'>
                  <DMImage src='/images/home/level-up.png' alt='coin Icon' width={20} height={20} />{' '}
                  {xp}
                </div>
              </motion.ul> */}
              <motion.ul
                initial='enter'
                animate='center'
                exit='exit'
                variants={presence}
                transition={{ duration: 0.2 }}
                className='text-brand-primary-hover mt-10 border-t border-brand-primary-border flex gap-2 w-[60%] text-sm items-center py-2'
              >
                <motion.li
                  variants={itemVariants}
                  className='border-r pr-2 border-brand-primary-border'
                  onClick={handleToggle}
                >
                  <DMLink href='/profile' className='flex gap-1 items-center text-slate-400'>
                    <User className='w-3 h-3' /> Profile
                  </DMLink>
                </motion.li>
                <motion.li variants={itemVariants}>
                  <div
                    onClick={async () => {
                      await signOut();
                    }}
                    className='flex gap-1 items-center text-slate-400 hover:text-brand-primary'
                  >
                    <LogOut className='w-3 h-3' />
                    Sign out
                  </div>
                </motion.li>
              </motion.ul>
            </>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Notch;
